import { DEFAULT_LOCALE, LOCALE_COOKIE, normalizeLocale, type Locale } from "./locale";
import type { CompiledMarkdownDocument, PublicProfileDocument } from "./types";

export const PUBLIC_ARTIFACT_SCHEMA_VERSION = 1;
export const PUBLIC_RENDERER_VERSION = "2";
export const PUBLIC_HTML_MAX_BYTES = 512 * 1024;
export const PUBLIC_OG_MAX_BYTES = 1024 * 1024;
export const PUBLIC_REACT_IDENTIFIER_PREFIX = "public-profile-";

export type PublicPageBootstrap = {
  locale: Locale;
  document: PublicProfileDocument;
  compiledMarkdown: CompiledMarkdownDocument;
};

export type PublicDeliveryMode = "origin" | "canary" | "artifact";
export type PublicRenderJobStatus = "queued" | "processing" | "succeeded" | "failed" | "cancelled";
export type PublicRenderJobKind = "publish" | "presentation_refresh" | "backfill";
export type D1BlobValue = readonly number[] | ArrayBuffer | ArrayBufferView;

export function publicHtmlArtifactKey(
  profileId: string,
  artifactRevision: number,
  rendererVersion: string,
  locale: Locale
): string {
  return `public:v${PUBLIC_ARTIFACT_SCHEMA_VERSION}:${profileId}:r${normalizeRevision(artifactRevision)}:${rendererVersion}:html:${locale}`;
}

export function publicOgArtifactKey(profileId: string, artifactRevision: number, rendererVersion: string): string {
  return `public:v${PUBLIC_ARTIFACT_SCHEMA_VERSION}:${profileId}:r${normalizeRevision(artifactRevision)}:${rendererVersion}:og`;
}

export function publicArtifactCacheUrl(key: string): string {
  return `https://public-artifacts.internal/${encodeURIComponent(key)}`;
}

export function d1BlobToArrayBuffer(value: D1BlobValue): ArrayBuffer {
  if (value instanceof ArrayBuffer) return value.slice(0);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength).slice().buffer;
  }
  return Uint8Array.from(value).buffer;
}

export function resolvePublicLocale(headers: Headers | { get(name: string): string | null }): Locale {
  const cookieLocale = readLocaleCookie(headers.get("cookie"));
  if (cookieLocale) return cookieLocale;
  return resolveLocaleFromAcceptLanguage(headers.get("accept-language")) ?? DEFAULT_LOCALE;
}

export function resolveLocaleFromAcceptLanguage(header?: string | null): Locale | null {
  if (!header) return null;
  const candidates = header
    .split(",")
    .map((item) => {
      const [rawLocale, ...parameters] = item.trim().split(";");
      const qualityRaw = parameters
        .map((parameter) => parameter.trim())
        .find((parameter) => /^q\s*=/i.test(parameter))
        ?.replace(/^q\s*=\s*/i, "");
      const quality = qualityRaw === undefined ? 1 : Number(qualityRaw);
      return { locale: normalizeLocale(rawLocale), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((candidate): candidate is { locale: Locale; quality: number } => Boolean(candidate.locale) && candidate.quality > 0)
    .sort((left, right) => right.quality - left.quality);
  return candidates[0]?.locale ?? null;
}

export function readLocaleCookie(cookieHeader?: string | null): Locale | null {
  if (!cookieHeader) return null;
  for (const cookie of cookieHeader.split(";")) {
    const [name, ...valueParts] = cookie.trim().split("=");
    if (name !== LOCALE_COOKIE) continue;
    try {
      return normalizeLocale(decodeURIComponent(valueParts.join("=")));
    } catch {
      return null;
    }
  }
  return null;
}

export function normalizePublicDeliveryMode(value?: string | null): PublicDeliveryMode {
  if (value === "origin" || value === "canary") return value;
  return "artifact";
}

export async function shouldUseArtifact(input: {
  mode: PublicDeliveryMode;
  percentage: number;
  profileId: string;
}): Promise<boolean> {
  if (input.mode === "origin") return false;
  if (input.mode === "artifact") return true;
  const percentage = Math.max(0, Math.min(100, Math.floor(input.percentage)));
  if (percentage === 0) return false;
  if (percentage === 100) return true;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input.profileId));
  const bucket = new DataView(digest).getUint32(0) % 100;
  return bucket < percentage;
}

export function quoteEtag(value: string): string {
  return `"${value.replaceAll('"', "")}"`;
}

function normalizeRevision(value: number): number {
  return Math.max(0, Math.floor(value));
}
